
import { elementsEqual } from 'lib/sceneEditorGeometry'
import { SceneElement } from 'types/api'

export type HistoryDomain = 'layout' | 'weights'
export type HistoryDirection = 'undo' | 'redo'

export const HISTORY_CAP = 100

export const NUDGE_COALESCE_MS = 1000

export interface WeightsSlot {
  pollId: string
  weights: number[] | null
  baseline: number[] | null
}

export interface WeightsSnapshot {
  slot: WeightsSlot | null
  stored: number[] | null
}

interface EntryMeta {
  label: string
  elementKey: string | null
  coalesceKey: string | null
  at: number
}

export interface LayoutEntry extends EntryMeta {
  domain: 'layout'
  pollId: null
  before: SceneElement[]
  after: SceneElement[]
}

export interface WeightsEntry extends EntryMeta {
  domain: 'weights'
  pollId: string
  before: WeightsSnapshot
  after: WeightsSnapshot
}

export type HistoryEntry = LayoutEntry | WeightsEntry

export interface SceneHistory {
  past: HistoryEntry[]
  future: HistoryEntry[]
}

export type SceneHistories = Record<string, SceneHistory>

export const EMPTY_HISTORY: SceneHistory = { past: [], future: [] }

export const weightListEq = (a: number[] | null, b: number[] | null): boolean => {
  if (a == null || b == null) return a === b
  return a.length === b.length && a.every((w, i) => w === b[i])
}

export const normalizeWeightsSlot = (slot: WeightsSlot | null): WeightsSlot | null =>
  slot == null || weightListEq(slot.weights, slot.baseline) ? null : slot

export const weightsSlotEq = (a: WeightsSlot | null, b: WeightsSlot | null): boolean => {
  if (a == null || b == null) return a === b
  return a.pollId === b.pollId && weightListEq(a.weights, b.weights) && weightListEq(a.baseline, b.baseline)
}

export const weightsSnapshotEq = (a: WeightsSnapshot, b: WeightsSnapshot): boolean =>
  weightsSlotEq(a.slot, b.slot) && (a.slot != null || weightListEq(a.stored, b.stored))

export const isNoopEntry = (entry: HistoryEntry): boolean =>
  entry.domain === 'layout'
    ? elementsEqual(entry.before, entry.after)
    : weightsSnapshotEq(entry.before, entry.after)

const continues = (top: HistoryEntry, next: HistoryEntry): boolean => {
  if (top.domain === 'layout' && next.domain === 'layout') return elementsEqual(top.after, next.before)
  if (top.domain === 'weights' && next.domain === 'weights') {
    return top.pollId === next.pollId && weightsSnapshotEq(top.after, next.before)
  }
  return false
}

export const recordEdit = (history: SceneHistory, entry: HistoryEntry): SceneHistory => {
  if (isNoopEntry(entry)) return history
  const top = history.past.length > 0 ? history.past[history.past.length - 1] : null
  if (entry.coalesceKey != null && top != null && history.future.length === 0 &&
    top.coalesceKey === entry.coalesceKey && entry.at - top.at <= NUDGE_COALESCE_MS && continues(top, entry)) {
    const rest = history.past.slice(0, -1)
    const merged: HistoryEntry = top.domain === 'layout' && entry.domain === 'layout'
      ? { ...top, after: entry.after, at: entry.at }
      : top.domain === 'weights' && entry.domain === 'weights'
        ? { ...top, after: entry.after, at: entry.at }
        : top
    return { past: isNoopEntry(merged) ? rest : [...rest, merged], future: [] }
  }
  const past = [...history.past, entry]
  return { past: past.length > HISTORY_CAP ? past.slice(past.length - HISTORY_CAP) : past, future: [] }
}

export const topEntryOf = (history: SceneHistory | undefined, direction: HistoryDirection): HistoryEntry | null => {
  if (history == null) return null
  const stack = direction === 'undo' ? history.past : history.future
  return stack.length > 0 ? stack[stack.length - 1] : null
}

export const undoStep = (history: SceneHistory): { history: SceneHistory, entry: HistoryEntry } | null => {
  const entry = topEntryOf(history, 'undo')
  if (entry == null) return null
  return { history: { past: history.past.slice(0, -1), future: [...history.future, entry] }, entry }
}

export const redoStep = (history: SceneHistory): { history: SceneHistory, entry: HistoryEntry } | null => {
  const entry = topEntryOf(history, 'redo')
  if (entry == null) return null
  return { history: { past: [...history.past, { ...entry, coalesceKey: null }], future: history.future.slice(0, -1) }, entry }
}

export const layoutEntryApplies = (entry: LayoutEntry, direction: HistoryDirection, current: SceneElement[]): boolean =>
  elementsEqual(current, direction === 'undo' ? entry.after : entry.before)

export const weightsEntryApplies = (entry: WeightsEntry, direction: HistoryDirection, current: WeightsSnapshot): boolean =>
  weightsSnapshotEq(current, direction === 'undo' ? entry.after : entry.before)

export const clearDomain = (history: SceneHistory, domain: HistoryDomain, pollId?: string): SceneHistory => {
  const keep = (e: HistoryEntry): boolean =>
    e.domain !== domain || (domain === 'weights' && pollId != null && e.pollId !== pollId)
  const past = history.past.filter(keep)
  const future = history.future.filter(keep)
  return past.length === history.past.length && future.length === history.future.length ? history : { past, future }
}

export const withSceneHistory = (histories: SceneHistories, sceneId: string, history: SceneHistory): SceneHistories => {
  if (histories[sceneId] === history) return histories
  if (history.past.length === 0 && history.future.length === 0) {
    if (histories[sceneId] == null) return histories
    const { [sceneId]: _dropped, ...rest } = histories
    return rest
  }
  return { ...histories, [sceneId]: history }
}

export const recordSceneEdit = (histories: SceneHistories, sceneId: string, entry: HistoryEntry): SceneHistories =>
  withSceneHistory(histories, sceneId, recordEdit(histories[sceneId] ?? EMPTY_HISTORY, entry))

export const clearSceneDomain = (histories: SceneHistories, sceneId: string, domain: HistoryDomain, pollId?: string): SceneHistories => {
  const history = histories[sceneId]
  return history == null ? histories : withSceneHistory(histories, sceneId, clearDomain(history, domain, pollId))
}

export const clearDomainEverywhere = (histories: SceneHistories, domain: HistoryDomain, pollId?: string): SceneHistories =>
  Object.keys(histories).reduce((acc, sceneId) => clearSceneDomain(acc, sceneId, domain, pollId), histories)

export const pruneScenes = (histories: SceneHistories, keep: (sceneId: string) => boolean): SceneHistories =>
  Object.keys(histories).reduce((acc, sceneId) => keep(sceneId) ? acc : withSceneHistory(acc, sceneId, EMPTY_HISTORY), histories)

export const pruneWeights = (histories: SceneHistories, keep: (pollId: string) => boolean): SceneHistories => {
  const dead = new Set<string>()
  for (const history of Object.values(histories)) {
    for (const e of [...history.past, ...history.future]) {
      if (e.domain === 'weights' && !keep(e.pollId)) dead.add(e.pollId)
    }
  }
  return [...dead].reduce((acc, pollId) => clearDomainEverywhere(acc, 'weights', pollId), histories)
}

export const stepSceneHistory = (histories: SceneHistories, sceneId: string, direction: HistoryDirection, entry: HistoryEntry): SceneHistories => {
  const history = histories[sceneId]
  if (history == null || topEntryOf(history, direction) !== entry) return histories
  const step = direction === 'undo' ? undoStep(history) : redoStep(history)
  return step == null ? histories : withSceneHistory(histories, sceneId, step.history)
}
